/**
 * Session swap for a head office acting as one of its member agencies.
 *
 * Deliberately SEPARATE from the admin "Prise de contrôle" (impersonationReducer): the
 * two mechanisms have different claims, different audit trails and different powers, and
 * sharing localStorage keys would let one restore the other's session. Distinct keys keep
 * them from ever colliding — including when a Guydeez admin is impersonating a head
 * office that then acts as one of its agencies.
 *
 * There is NO refresh token for a network session: the backend mints a 30-minute access
 * token and nothing else, so the session expires rather than renewing itself. When it
 * does, the head office lands back on its own account by pressing "stop".
 */

export const NETWORK_ACTING_KEY = 'gd_network_acting';
const ORIGINAL_TOKEN_KEY = 'gd_network_original_token';
const ORIGINAL_REFRESH_KEY = 'gd_network_original_refresh';
const ORIGINAL_USER_KEY = 'gd_network_original_user';

export type NetworkActingState = {
  memberUuid: string;
  memberName: string | null;
  actingLogUuid: string;
  startedAt: number;
};

const read = (k: string) => {
  try { return window.localStorage.getItem(k); } catch { return null; }
};
const write = (k: string, v: string | null) => {
  try {
    if (v === null) window.localStorage.removeItem(k);
    else window.localStorage.setItem(k, v);
  } catch { /* private mode: the swap simply does not persist */ }
};

export const readActingState = (): NetworkActingState | null => {
  if (typeof window === 'undefined') return null;
  const raw = read(NETWORK_ACTING_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as NetworkActingState; } catch { return null; }
};

/**
 * Swap the browser onto the member's session, keeping the head office's own tokens aside
 * so "stop" is a restore and not a re-login.
 */
export const beginActing = (
  accessToken: string,
  meta: Omit<NetworkActingState, 'startedAt'>
) => {
  if (typeof window === 'undefined') return;
  // Saved BEFORE anything is overwritten: if this throws we must not have destroyed the
  // only copy of the head office's session.
  write(ORIGINAL_TOKEN_KEY, read('access_token'));
  write(ORIGINAL_REFRESH_KEY, read('refresh_token'));
  write(ORIGINAL_USER_KEY, read('user'));

  write('access_token', accessToken);
  // The network token has no refresh counterpart. Leaving the head office's refresh
  // token in place would let the api interceptor silently renew the WRONG session and
  // hand the agency's tab a head-office token.
  write('refresh_token', null);
  write(NETWORK_ACTING_KEY, JSON.stringify({ ...meta, startedAt: Date.now() }));
};

/**
 * Jette la couche reseau SANS rien restaurer.
 *
 * A ne pas confondre avec `endActing()`, qui repose les jetons du siege. Ici on
 * veut oublier la prise de main, pas y revenir : le cas est celui d'un
 * administrateur en prise de controle sur un siege qui agissait pour une de ses
 * agences, et qui quitte la couche EXTERIEURE. L'identite a laquelle cette
 * couche appartenait vient de disparaitre ; la restaurer remettrait le jeton du
 * siege dans une session redevenue celle de l'administrateur.
 *
 * Sans cela la barre « Vous agissez au nom de X » survivait a la sortie
 * d'impersonation, et son bouton faisait basculer l'administrateur dans le
 * compte du siege.
 */
export const discardActing = () => {
  if (typeof window === 'undefined') return;
  write(ORIGINAL_TOKEN_KEY, null);
  write(ORIGINAL_REFRESH_KEY, null);
  write(ORIGINAL_USER_KEY, null);
  write(NETWORK_ACTING_KEY, null);
};

export const endActing = () => {
  if (typeof window === 'undefined') return;
  const token = read(ORIGINAL_TOKEN_KEY);
  const refresh = read(ORIGINAL_REFRESH_KEY);
  const user = read(ORIGINAL_USER_KEY);
  if (token) write('access_token', token);
  write('refresh_token', refresh);
  if (user) write('user', user);
  write(ORIGINAL_TOKEN_KEY, null);
  write(ORIGINAL_REFRESH_KEY, null);
  write(ORIGINAL_USER_KEY, null);
  write(NETWORK_ACTING_KEY, null);
};
